import { supabase } from '../lib/supabase';

export interface CalculatedStatsResult {
  [statKey: string]: number;
}

class CalculatedStatsService {

  /**
   * Calculate all Attack category stats for a team in a match
   */
  static async calculateAttackStats(matchId: string, teamId: string): Promise<CalculatedStatsResult> {
    try {
      // Fetch all relevant stats for the team
      const { data: stats, error } = await supabase
        .from('scoresheet')
        .select(`
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, display_name)
        `)
        .eq('match_id', matchId)
        .eq('team_id', teamId);

      if (error) {
        throw new Error(`Failed to fetch stats: ${error.message}`);
      }

      // Convert to a map for easy access
      const statsMap = new Map<string, number>();
      stats?.forEach(stat => {
        if (stat.stat_type && typeof stat.stat_type === 'object' && 'stat_key' in stat.stat_type) {
          statsMap.set(stat.stat_type.stat_key as string, stat.value || 0);
        }
      });

      // Calculate Attack stats
      const calculatedStats: CalculatedStatsResult = {};

      // Total Goals: goals inside box + goals outside box + penalty + own goal
      // Note: own_goal is now recorded under the attacking team (team that benefits from it)
      calculatedStats.total_goals = 
        (statsMap.get('shot_inside_box_goal') || 0) +
        (statsMap.get('shot_outside_box_goal') || 0) +
        (statsMap.get('penalty_scored') || 0) +
        (statsMap.get('own_goal') || 0);

      // Total shots on target: goals + shots on target (excluding goals)
      const shotsOnTarget = 
        (statsMap.get('shot_inside_box_goal') || 0) +
        (statsMap.get('shot_outside_box_goal') || 0) +
        (statsMap.get('shot_inside_box_on_target') || 0) +
        (statsMap.get('shot_outside_box_on_target') || 0);

      // Total shots off target: all off-target shots + blocked + woodwork
      const shotsOffTarget = 
        (statsMap.get('shot_inside_box_off_target') || 0) +
        (statsMap.get('shot_outside_box_off_target') || 0) +
        (statsMap.get('shot_inside_box_crossbar') || 0) +
        (statsMap.get('shot_outside_box_crossbar') || 0) +
        (statsMap.get('shot_inside_box_side_post') || 0) +
        (statsMap.get('shot_outside_box_side_post') || 0) +
        (statsMap.get('shot_inside_box_blocked') || 0) +
        (statsMap.get('shot_outside_box_blocked') || 0);

      // Total shots: comprehensive calculation including all shot-related events
      const totalShots = 
        // Inside box shots
        (statsMap.get('shot_inside_box_on_target') || 0) +
        (statsMap.get('shot_inside_box_off_target') || 0) +
        (statsMap.get('shot_inside_box_blocked') || 0) +
        (statsMap.get('shot_inside_box_side_post') || 0) +
        (statsMap.get('shot_inside_box_crossbar') || 0) +
        (statsMap.get('shot_inside_box_goal') || 0) +
        // Outside box shots
        (statsMap.get('shot_outside_box_on_target') || 0) +
        (statsMap.get('shot_outside_box_off_target') || 0) +
        (statsMap.get('shot_outside_box_blocked') || 0) +
        (statsMap.get('shot_outside_box_side_post') || 0) +
        (statsMap.get('shot_outside_box_crossbar') || 0) +
        (statsMap.get('shot_outside_box_goal') || 0) +
        // Penalties and free kicks
        (statsMap.get('penalty_scored') || 0) +
        (statsMap.get('penalty_missed') || 0) +
        (statsMap.get('direct_fk') || 0);
      calculatedStats.total_shots = totalShots;

      // Shots on target
      calculatedStats.shots_on_target = shotsOnTarget;

      // Shots off target
      calculatedStats.shots_off_target = shotsOffTarget;

      // Goals conversion (%): total goals/total shots * 100
      calculatedStats.goals_conversion_percent = totalShots > 0 
        ? Math.round((calculatedStats.total_goals / totalShots) * 100) // Round to integer
        : 0;

      // Shots accuracy (%): shots on target / total shots * 100
      calculatedStats.shots_accuracy_percent = totalShots > 0 
        ? Math.round((shotsOnTarget / totalShots) * 100) // Round to integer
        : 0;

      // Penalties awarded: penalty missed + penalty scored
      calculatedStats.penalties_awarded = 
        (statsMap.get('penalty_scored') || 0) +
        (statsMap.get('penalty_missed') || 0);

      // Attacks: total shots + crosses + direct free kick + indirect free kick + corner + penalties awarded
      calculatedStats.attacks = 
        totalShots +
        (statsMap.get('cross_success') || 0) +
        (statsMap.get('cross_failure') || 0) +
        (statsMap.get('direct_fk') || 0) +
        (statsMap.get('indirect_fk') || 0) +
        (statsMap.get('corner') || 0) +
        calculatedStats.penalties_awarded;

      // New calculated stats: Shots from inside/outside the box
      // Shots from inside the box = shots inside box on target + off target + side post + crossbar + goal + penalty scored + penalty missed
      calculatedStats.shots_from_inside_the_box = 
        (statsMap.get('shot_inside_box_on_target') || 0) +
        (statsMap.get('shot_inside_box_off_target') || 0) +
        (statsMap.get('shot_inside_box_side_post') || 0) +
        (statsMap.get('shot_inside_box_crossbar') || 0) +
        (statsMap.get('shot_inside_box_goal') || 0) +
        (statsMap.get('penalty_scored') || 0) +
        (statsMap.get('penalty_missed') || 0);

      // Shots from outside the box = shots outside box on target + off target + side post + crossbar + goal + direct free kick
      calculatedStats.shots_from_outside_the_box = 
        (statsMap.get('shot_outside_box_on_target') || 0) +
        (statsMap.get('shot_outside_box_off_target') || 0) +
        (statsMap.get('shot_outside_box_side_post') || 0) +
        (statsMap.get('shot_outside_box_crossbar') || 0) +
        (statsMap.get('shot_outside_box_goal') || 0) +
        (statsMap.get('direct_fk') || 0);

      // Number of crosses = cross success + cross failure
      calculatedStats.number_of_crosses = 
        (statsMap.get('cross_success') || 0) +
        (statsMap.get('cross_failure') || 0);

      // Crossing accuracy (%) = (cross success / number of crosses) * 100
      const numberOfCrosses = calculatedStats.number_of_crosses;
      const crossSuccess = statsMap.get('cross_success') || 0;
      calculatedStats.crossing_accuracy_percent = numberOfCrosses > 0 
        ? Math.round((crossSuccess / numberOfCrosses) * 100) // Round to integer
        : 0;

      return calculatedStats;
    } catch (error) {
      console.error('Error calculating Attack stats:', error);
      throw error;
    }
  }

  /**
   * Save calculated stats to the scoresheet table
   */
  static async saveCalculatedStats(matchId: string, teamId: string, calculatedStats: CalculatedStatsResult): Promise<void> {
    try {
      // Get calculated stat type IDs
      const { data: statTypes, error: statTypesError } = await supabase
        .from('stat_types')
        .select('id, stat_key')
        .eq('is_calculated', true)
        .in('stat_key', Object.keys(calculatedStats));

      if (statTypesError) {
        throw new Error(`Failed to fetch stat types: ${statTypesError.message}`);
      }

      // Upsert calculated stats (update if exists, insert if not)
      const upsertPromises = statTypes?.map(async (statType) => {
        const value = calculatedStats[statType.stat_key];
        if (value !== undefined) {
          const { error } = await supabase
            .from('scoresheet')
            .upsert({
              match_id: matchId,
              team_id: teamId,
              stat_type_id: statType.id,
              value: value,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'match_id,team_id,stat_type_id'
            });

          if (error) {
            throw new Error(`Failed to save ${statType.stat_key}: ${error.message}`);
          }
        }
      });

      await Promise.all(upsertPromises || []);
    } catch (error) {
      console.error('Error saving calculated stats:', error);
      throw error;
    }
  }

  /**
   * Calculate and save all Attack stats for a team
   */
  static async calculateAndSaveAttackStats(matchId: string, teamId: string): Promise<void> {
    try {
      const calculatedStats = await this.calculateAttackStats(matchId, teamId);
      await this.saveCalculatedStats(matchId, teamId, calculatedStats);
    } catch (error) {
      console.error('Error calculating and saving Attack stats:', error);
      throw error;
    }
  }

  /**
   * Calculate all Distribution category stats for a team in a match
   */
  static async calculateDistributionStats(matchId: string, teamId: string): Promise<CalculatedStatsResult> {
    try {
      // Fetch all relevant stats for the team
      const { data: stats, error } = await supabase
        .from('scoresheet')
        .select(`
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, display_name)
        `)
        .eq('match_id', matchId)
        .eq('team_id', teamId);

      if (error) {
        throw new Error(`Failed to fetch stats: ${error.message}`);
      }

      // Convert to a map for easy access
      const statsMap = new Map<string, number>();
      stats?.forEach(stat => {
        if (stat.stat_type && typeof stat.stat_type === 'object' && 'stat_key' in stat.stat_type) {
          statsMap.set(stat.stat_type.stat_key as string, stat.value || 0);
        }
      });

      // Calculate Distribution stats
      const calculatedStats: CalculatedStatsResult = {};

      // Passes completed: short pass success forward & backward + long pass success + cross success
      const passesCompleted = 
        (statsMap.get('short_pass_success') || 0) +
        (statsMap.get('short_pass_backward_success') || 0) +
        (statsMap.get('long_pass_success') || 0) +
        (statsMap.get('long_pass_backward_success') || 0) +
        (statsMap.get('cross_success') || 0) +
        (statsMap.get('cross_backward_success') || 0);

      // Passes failed: short pass fail forward & backward + long pass fail + cross fail
      const passesFailed = 
        (statsMap.get('short_pass_failure') || 0) +
        (statsMap.get('short_pass_backward_failure') || 0) +
        (statsMap.get('long_pass_failure') || 0) +
        (statsMap.get('long_pass_backward_failure') || 0) +
        (statsMap.get('cross_failure') || 0) +
        (statsMap.get('cross_backward_failure') || 0);

      // Passes attempted: Passes completed + passes failed
      const passesAttempted = passesCompleted + passesFailed;
      calculatedStats.passes_attempted = passesAttempted;
      calculatedStats.passes_completed = passesCompleted;
      calculatedStats.passes_failed = passesFailed;

      // Passing accuracy (%): passes completed / passes attempted * 100
      calculatedStats.passing_accuracy_percent = passesAttempted > 0 
        ? Math.round((passesCompleted / passesAttempted) * 100) // Round to integer
        : 0;

      // Short passes attempted: short pass success + short pass fail for forward and backward
      const shortPassesAttempted = 
        (statsMap.get('short_pass_success') || 0) +
        (statsMap.get('short_pass_failure') || 0) +
        (statsMap.get('short_pass_backward_success') || 0) +
        (statsMap.get('short_pass_backward_failure') || 0);
      calculatedStats.short_passes_attempted = shortPassesAttempted;

      // Short pass accuracy (%): short pass success forward & backward / short pass attempted * 100
      const shortPassesCompleted = 
        (statsMap.get('short_pass_success') || 0) +
        (statsMap.get('short_pass_backward_success') || 0);
      calculatedStats.short_pass_accuracy_percent = shortPassesAttempted > 0 
        ? Math.round((shortPassesCompleted / shortPassesAttempted) * 100) // Round to integer
        : 0;

      // Backward pass (%): backward pass success / short pass attempted * 100
      const backwardPassesCompleted = 
        (statsMap.get('short_pass_backward_success') || 0) +
        (statsMap.get('long_pass_backward_success') || 0) +
        (statsMap.get('cross_backward_success') || 0);
      calculatedStats.backward_pass_percent = shortPassesAttempted > 0 
        ? Math.round((backwardPassesCompleted / shortPassesAttempted) * 100) // Round to integer
        : 0;

      // Number of backward passes: short pass backward success + short pass backward failure
      calculatedStats.backward_passes = 
        (statsMap.get('short_pass_backward_success') || 0) +
        (statsMap.get('short_pass_backward_failure') || 0);

      // Long passes attempted: Long passes completed + Long passes failed
      const longPassesAttempted = 
        (statsMap.get('long_pass_success') || 0) +
        (statsMap.get('long_pass_failure') || 0) +
        (statsMap.get('long_pass_backward_success') || 0) +
        (statsMap.get('long_pass_backward_failure') || 0);
      calculatedStats.long_passes_attempted = longPassesAttempted;

      // Long pass accuracy (%): Long passes success / long pass attempted * 100
      const longPassesCompleted = 
        (statsMap.get('long_pass_success') || 0) +
        (statsMap.get('long_pass_backward_success') || 0);
      calculatedStats.long_pass_accuracy_percent = longPassesAttempted > 0 
        ? Math.round((longPassesCompleted / longPassesAttempted) * 100) // Round to integer
        : 0;

      // Crosses attempted: Crosses completed + Crosses failed
      const crossesAttempted = 
        (statsMap.get('cross_success') || 0) +
        (statsMap.get('cross_failure') || 0) +
        (statsMap.get('cross_backward_success') || 0) +
        (statsMap.get('cross_backward_failure') || 0);
      calculatedStats.crosses_attempted = crossesAttempted;

      // Crossing accuracy (%): Crosses success / Crosses attempted * 100
      const crossesCompleted = 
        (statsMap.get('cross_success') || 0) +
        (statsMap.get('cross_backward_success') || 0);
      calculatedStats.crossing_accuracy_percent = crossesAttempted > 0 
        ? Math.round((crossesCompleted / crossesAttempted) * 100) // Round to integer
        : 0;

      // Key passes: shot on target inside box outside box + shot on sidepost & crossbar from inside box + outside box
      calculatedStats.key_passes = 
        (statsMap.get('shot_inside_box_on_target') || 0) +
        (statsMap.get('shot_outside_box_on_target') || 0) +
        (statsMap.get('shot_inside_box_side_post') || 0) +
        (statsMap.get('shot_outside_box_side_post') || 0) +
        (statsMap.get('shot_inside_box_crossbar') || 0) +
        (statsMap.get('shot_outside_box_crossbar') || 0);

      // Chances created: key pass + total goals
      const totalGoals = 
        (statsMap.get('shot_inside_box_goal') || 0) +
        (statsMap.get('shot_outside_box_goal') || 0) +
        (statsMap.get('penalty_scored') || 0) +
        (statsMap.get('own_goal') || 0);
      calculatedStats.chances_created = calculatedStats.key_passes + totalGoals;

      // Note: Possession calculation requires both teams' data, will be handled separately
      calculatedStats.possession_percent = 0; // Placeholder - needs both teams' data

      return calculatedStats;
    } catch (error) {
      console.error('Error calculating Distribution stats:', error);
      throw error;
    }
  }

  /**
   * Calculate possession percentage for both teams
   */
  static async calculatePossession(matchId: string, homeTeamId: string, awayTeamId: string): Promise<{home: number, away: number}> {
    try {
      // Fetch all stats for both teams
      const { data: stats, error } = await supabase
        .from('scoresheet')
        .select(`
          team_id,
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, display_name, category)
        `)
        .eq('match_id', matchId)
        .in('team_id', [homeTeamId, awayTeamId]);

      if (error) {
        throw new Error(`Failed to fetch stats: ${error.message}`);
      }

      // Calculate total actions for each team (excluding percentage stats)
      const homeTeamActions = new Map<string, number>();
      const awayTeamActions = new Map<string, number>();

      stats?.forEach(stat => {
        if (stat.stat_type && typeof stat.stat_type === 'object' && 
            'stat_key' in stat.stat_type && 'category' in stat.stat_type &&
            !(stat.stat_type.stat_key as string).includes('percent') && 
            !(stat.stat_type.stat_key as string).includes('accuracy')) {
          
          const statKey = stat.stat_type.stat_key as string;
          if (stat.team_id === homeTeamId) {
            homeTeamActions.set(statKey, (homeTeamActions.get(statKey) || 0) + (stat.value || 0));
          } else if (stat.team_id === awayTeamId) {
            awayTeamActions.set(statKey, (awayTeamActions.get(statKey) || 0) + (stat.value || 0));
          }
        }
      });

      // Sum all actions for each team
      const homeTotal = Array.from(homeTeamActions.values()).reduce((sum, value) => sum + value, 0);
      const awayTotal = Array.from(awayTeamActions.values()).reduce((sum, value) => sum + value, 0);
      const grandTotal = homeTotal + awayTotal;

      // Calculate possession percentages
      const homePossession = grandTotal > 0 ? Math.round((homeTotal / grandTotal) * 100) : 0; // Round to integer
      const awayPossession = grandTotal > 0 ? Math.round((awayTotal / grandTotal) * 100) : 0; // Round to integer

      return { home: homePossession, away: awayPossession };
    } catch (error) {
      console.error('Error calculating possession:', error);
      throw error;
    }
  }

  /**
   * Calculate and save all Distribution stats for a team
   */
  static async calculateAndSaveDistributionStats(matchId: string, teamId: string): Promise<void> {
    try {
      const calculatedStats = await this.calculateDistributionStats(matchId, teamId);
      await this.saveCalculatedStats(matchId, teamId, calculatedStats);
    } catch (error) {
      console.error('Error calculating and saving Distribution stats:', error);
      throw error;
    }
  }

  /**
   * Calculate all Defense category stats for a team in a match
   */
  static async calculateDefenseStats(matchId: string, teamId: string): Promise<CalculatedStatsResult> {
    try {
      // Fetch all relevant stats for the team
      const { data: stats, error } = await supabase
        .from('scoresheet')
        .select(`
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, display_name)
        `)
        .eq('match_id', matchId)
        .eq('team_id', teamId);

      if (error) {
        throw new Error(`Failed to fetch stats: ${error.message}`);
      }

      // Convert to a map for easy access
      const statsMap = new Map<string, number>();
      stats?.forEach(stat => {
        if (stat.stat_type && typeof stat.stat_type === 'object' && 'stat_key' in stat.stat_type) {
          statsMap.set(stat.stat_type.stat_key as string, stat.value || 0);
        }
      });

      // Calculate Defense stats
      const calculatedStats: CalculatedStatsResult = {};

      // Balls recovered: tackles won + aerial duels won + blocks + interceptions
      calculatedStats.balls_recovered = 
        (statsMap.get('tackle_success') || 0) +
        (statsMap.get('aerial_won') || 0) +
        (statsMap.get('shot_inside_box_blocked') || 0) +
        (statsMap.get('shot_outside_box_blocked') || 0) +
        (statsMap.get('interception') || 0);

      // Tackles attempted: Tackles won + tackles fail
      const tacklesAttempted = 
        (statsMap.get('tackle_success') || 0) +
        (statsMap.get('tackle_failure') || 0);
      calculatedStats.tackles_attempted = tacklesAttempted;

      // Tackles accuracy (%): Tackles won / Tackles attempted * 100
      const tacklesWon = statsMap.get('tackle_success') || 0;
      calculatedStats.tackles_accuracy_percent = tacklesAttempted > 0 
        ? Math.round((tacklesWon / tacklesAttempted) * 100) // Round to integer
        : 0;

      // Fouls committed: Fouls committed in opponent half + Fouls committed in own half + Red cards + Yellow cards
      calculatedStats.fouls_committed = 
        (statsMap.get('foul_opp_half') || 0) +
        (statsMap.get('foul_own_half') || 0) +
        (statsMap.get('red_card') || 0) +
        (statsMap.get('yellow_card') || 0);

      // Note: Penalties conceded requires opponent team data, will be handled separately
      calculatedStats.penalties_conceded = 0; // Placeholder - needs opponent team data

      return calculatedStats;
    } catch (error) {
      console.error('Error calculating Defense stats:', error);
      throw error;
    }
  }

  /**
   * Calculate all Goalkeeper category stats for a team in a match
   */
  static async calculateGoalkeeperStats(matchId: string, teamId: string): Promise<CalculatedStatsResult> {
    try {
      // Fetch all relevant stats for the team
      const { data: stats, error } = await supabase
        .from('scoresheet')
        .select(`
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, display_name)
        `)
        .eq('match_id', matchId)
        .eq('team_id', teamId);

      if (error) {
        throw new Error(`Failed to fetch stats: ${error.message}`);
      }

      // Convert to a map for easy access
      const statsMap = new Map<string, number>();
      stats?.forEach(stat => {
        if (stat.stat_type && typeof stat.stat_type === 'object' && 'stat_key' in stat.stat_type) {
          statsMap.set(stat.stat_type.stat_key as string, stat.value || 0);
        }
      });

      // Calculate Goalkeeper stats
      const calculatedStats: CalculatedStatsResult = {};

      // Claims: claims of open play + claims of penalties
      calculatedStats.total_claims = 
        (statsMap.get('open_play_claim') || 0) +
        (statsMap.get('penalty_claim') || 0);

      // Punches made: punches of open play + punches of penalties
      calculatedStats.total_punches = 
        (statsMap.get('open_play_punch') || 0) +
        (statsMap.get('penalty_punch') || 0);

      // Total saves: total_claims + total_punches
      calculatedStats.total_saves = 
        calculatedStats.total_claims +
        calculatedStats.total_punches;

      // Note: Goals conceded requires opponent team data, will be handled separately
      calculatedStats.goals_conceded = 0; // Placeholder - needs opponent team data

      return calculatedStats;
    } catch (error) {
      console.error('Error calculating Goalkeeper stats:', error);
      throw error;
    }
  }

  /**
   * Calculate penalties conceded and goals conceded (requires opponent team data)
   */
  static async calculateOpponentStats(matchId: string, homeTeamId: string, awayTeamId: string): Promise<void> {
    try {
      // Fetch all stats for both teams
      const { data: stats, error } = await supabase
        .from('scoresheet')
        .select(`
          team_id,
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, display_name)
        `)
        .eq('match_id', matchId)
        .in('team_id', [homeTeamId, awayTeamId]);

      if (error) {
        throw new Error(`Failed to fetch stats: ${error.message}`);
      }

      // Calculate penalties conceded and goals conceded for each team
      const homeTeamStats = new Map<string, number>();
      const awayTeamStats = new Map<string, number>();

      stats?.forEach(stat => {
        if (stat.stat_type && typeof stat.stat_type === 'object' && 'stat_key' in stat.stat_type) {
          const statKey = stat.stat_type.stat_key as string;
          if (stat.team_id === homeTeamId) {
            homeTeamStats.set(statKey, stat.value || 0);
          } else if (stat.team_id === awayTeamId) {
            awayTeamStats.set(statKey, stat.value || 0);
          }
        }
      });

      // Calculate penalties conceded (opponent's penalties)
      const homePenaltiesConceded = 
        (awayTeamStats.get('penalty_scored') || 0) +
        (awayTeamStats.get('penalty_missed') || 0);
      
      const awayPenaltiesConceded = 
        (homeTeamStats.get('penalty_scored') || 0) +
        (homeTeamStats.get('penalty_missed') || 0);

      // Calculate goals conceded (opponent's goals)
      const homeGoalsConceded = 
        (awayTeamStats.get('shot_inside_box_goal') || 0) +
        (awayTeamStats.get('shot_outside_box_goal') || 0) +
        (awayTeamStats.get('penalty_scored') || 0) +
        (awayTeamStats.get('own_goal') || 0);
      
      const awayGoalsConceded = 
        (homeTeamStats.get('shot_inside_box_goal') || 0) +
        (homeTeamStats.get('shot_outside_box_goal') || 0) +
        (homeTeamStats.get('penalty_scored') || 0) +
        (homeTeamStats.get('own_goal') || 0);

      // Save penalties conceded
      await this.saveOpponentStat(matchId, homeTeamId, 'penalties_conceded', homePenaltiesConceded);
      await this.saveOpponentStat(matchId, awayTeamId, 'penalties_conceded', awayPenaltiesConceded);

      // Save goals conceded
      await this.saveOpponentStat(matchId, homeTeamId, 'goals_conceded', homeGoalsConceded);
      await this.saveOpponentStat(matchId, awayTeamId, 'goals_conceded', awayGoalsConceded);

    } catch (error) {
      console.error('Error calculating opponent stats:', error);
      throw error;
    }
  }

  /**
   * Save opponent-specific stats (penalties conceded, goals conceded)
   */
  private static async saveOpponentStat(matchId: string, teamId: string, statKey: string, value: number): Promise<void> {
    try {
      // Get stat type ID
      const { data: statType, error: statTypeError } = await supabase
        .from('stat_types')
        .select('id')
        .eq('stat_key', statKey)
        .eq('is_calculated', true)
        .single();

      if (statTypeError) {
        throw new Error(`Failed to fetch stat type ${statKey}: ${statTypeError.message}`);
      }

      // Upsert the stat
      const { error } = await supabase
        .from('scoresheet')
        .upsert({
          match_id: matchId,
          team_id: teamId,
          stat_type_id: statType.id,
          value: value,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'match_id,team_id,stat_type_id'
        });

      if (error) {
        throw new Error(`Failed to save ${statKey}: ${error.message}`);
      }
    } catch (error) {
      console.error(`Error saving opponent stat ${statKey}:`, error);
      throw error;
    }
  }

  /**
   * Calculate and save all Defense stats for a team
   */
  static async calculateAndSaveDefenseStats(matchId: string, teamId: string): Promise<void> {
    try {
      const calculatedStats = await this.calculateDefenseStats(matchId, teamId);
      await this.saveCalculatedStats(matchId, teamId, calculatedStats);
    } catch (error) {
      console.error('Error calculating and saving Defense stats:', error);
      throw error;
    }
  }

  /**
   * Calculate and save all Goalkeeper stats for a team
   */
  static async calculateAndSaveGoalkeeperStats(matchId: string, teamId: string): Promise<void> {
    try {
      const calculatedStats = await this.calculateGoalkeeperStats(matchId, teamId);
      await this.saveCalculatedStats(matchId, teamId, calculatedStats);
    } catch (error) {
      console.error('Error calculating and saving Goalkeeper stats:', error);
      throw error;
    }
  }

  /**
   * Calculate and save all stats for both teams using a robust queue system
   */
  static async calculateAndSaveAllStats(matchId: string, homeTeamId: string, awayTeamId: string): Promise<void> {
    try {
      console.log(`🚀 Starting calculation queue for match ${matchId}`);
      
      // Define all required calculations with validation
      const requiredCalculations = [
        { name: 'Home Attack', fn: () => this.calculateAndSaveAttackStats(matchId, homeTeamId), team: 'home', category: 'attack' },
        { name: 'Away Attack', fn: () => this.calculateAndSaveAttackStats(matchId, awayTeamId), team: 'away', category: 'attack' },
        { name: 'Home Distribution', fn: () => this.calculateAndSaveDistributionStats(matchId, homeTeamId), team: 'home', category: 'distribution' },
        { name: 'Away Distribution', fn: () => this.calculateAndSaveDistributionStats(matchId, awayTeamId), team: 'away', category: 'distribution' },
        { name: 'Home Defense', fn: () => this.calculateAndSaveDefenseStats(matchId, homeTeamId), team: 'home', category: 'defense' },
        { name: 'Away Defense', fn: () => this.calculateAndSaveDefenseStats(matchId, awayTeamId), team: 'away', category: 'defense' },
        { name: 'Home Goalkeeper', fn: () => this.calculateAndSaveGoalkeeperStats(matchId, homeTeamId), team: 'home', category: 'goalkeeper' },
        { name: 'Away Goalkeeper', fn: () => this.calculateAndSaveGoalkeeperStats(matchId, awayTeamId), team: 'away', category: 'goalkeeper' },
        { name: 'Opponent Stats', fn: () => this.calculateOpponentStats(matchId, homeTeamId, awayTeamId), team: 'both', category: 'opponent' },
        { name: 'Possession Stats', fn: () => this.calculatePossession(matchId, homeTeamId, awayTeamId).then(possession => 
          this.savePossessionStats(matchId, homeTeamId, awayTeamId, possession)
        ), team: 'both', category: 'possession' }
      ];

      // Execute calculations with comprehensive error handling and validation
      const results = await this.executeCalculationQueueWithValidation(requiredCalculations);
      
      // Validate that all critical calculations completed
      await this.validateCalculationResults(matchId, results);

      console.log(`🎉 All calculations completed successfully for match ${matchId}`);

      // Update match scores after all calculations are complete
      await this.updateMatchScores(matchId, homeTeamId, awayTeamId);

    } catch (error) {
      console.error('❌ Error calculating and saving all stats:', error);
      throw error;
    }
  }

  /**
   * Execute calculation queue with comprehensive error handling and validation
   */
  private static async executeCalculationQueueWithValidation(calculations: Array<{
    name: string;
    fn: () => Promise<any>;
    team: string;
    category: string;
  }>): Promise<Array<{ name: string; success: boolean; error?: any; attempts: number }>> {
    
    const results: Array<{ name: string; success: boolean; error?: any; attempts: number }> = [];
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second

    console.log(`📊 Executing ${calculations.length} calculations with validation`);

    // Execute each calculation with individual retry logic
    for (let i = 0; i < calculations.length; i++) {
      const calc = calculations[i];
      let lastError: any = null;
      let success = false;
      let attempts = 0;

      console.log(`🔄 [${i + 1}/${calculations.length}] Starting ${calc.name}`);

      // Retry logic with exponential backoff
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        attempts = attempt;
        try {
          console.log(`  📝 ${calc.name} (attempt ${attempt}/${maxRetries})`);
          await calc.fn();
          console.log(`  ✅ ${calc.name} completed successfully`);
          success = true;
          break;
        } catch (error) {
          lastError = error;
          console.error(`  ❌ ${calc.name} failed (attempt ${attempt}/${maxRetries}):`, error);
          
          if (attempt < maxRetries) {
            const delay = retryDelay * attempt; // Exponential backoff
            console.log(`  ⏳ Retrying ${calc.name} in ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }

      // Record result
      results.push({ 
        name: calc.name, 
        success, 
        error: lastError, 
        attempts 
      });

      if (!success) {
        console.error(`💥 ${calc.name} failed after ${maxRetries} attempts`);
      }
    }

    // Log comprehensive summary
    const successful = results.filter(r => r.success).length;
    const failed = results.filter(r => !r.success).length;
    console.log(`📈 Calculation Summary: ${successful} successful, ${failed} failed`);

    if (failed > 0) {
      const failedTasks = results.filter(r => !r.success).map(r => r.name);
      console.error(`⚠️ Failed calculations:`, failedTasks);
    }

    return results;
  }

  /**
   * Validate that all critical calculations completed successfully
   */
  private static async validateCalculationResults(matchId: string, results: Array<{ name: string; success: boolean; error?: any }>): Promise<void> {
    const failedCalculations = results.filter(r => !r.success);
    
    if (failedCalculations.length > 0) {
      console.error(`🚨 Validation failed: ${failedCalculations.length} calculations failed`);
      
      // Log detailed failure information
      failedCalculations.forEach(calc => {
        console.error(`  ❌ ${calc.name}: ${calc.error?.message || 'Unknown error'}`);
      });

      // For now, we'll log the failures but not throw an error
      // This allows the system to continue functioning even if some calculations fail
      console.warn(`⚠️ Continuing despite ${failedCalculations.length} failed calculations`);
    } else {
      console.log(`✅ All calculations validated successfully`);
    }
  }

  /**
   * Save possession stats for both teams
   */
  private static async savePossessionStats(matchId: string, homeTeamId: string, awayTeamId: string, possession: {home: number, away: number}): Promise<void> {
    try {
      // Get possession stat type ID
      const { data: statType, error: statTypeError } = await supabase
        .from('stat_types')
        .select('id')
        .eq('stat_key', 'possession_percent')
        .eq('is_calculated', true)
        .single();

      if (statTypeError) {
        throw new Error(`Failed to fetch possession stat type: ${statTypeError.message}`);
      }

      // Save possession for both teams
      await Promise.all([
        supabase.from('scoresheet').upsert({
          match_id: matchId,
          team_id: homeTeamId,
          stat_type_id: statType.id,
          value: possession.home,
          updated_at: new Date().toISOString()
        }, { onConflict: 'match_id,team_id,stat_type_id' }),
        
        supabase.from('scoresheet').upsert({
          match_id: matchId,
          team_id: awayTeamId,
          stat_type_id: statType.id,
          value: possession.away,
          updated_at: new Date().toISOString()
        }, { onConflict: 'match_id,team_id,stat_type_id' })
      ]);

    } catch (error) {
      console.error('Error saving possession stats:', error);
      throw error;
    }
  }

  /**
   * Update match scores in the matches table based on total goals from scoresheet
   * This method calculates total goals for both teams and updates the match record
   * Can be called independently or as part of calculateAndSaveAllStats
   */
  static async updateMatchScores(matchId: string, homeTeamId: string, awayTeamId: string): Promise<void> {
    try {
      console.log(`🏆 Updating match scores for match ${matchId}`);

      // Get total goals for home team
      const homeGoals = await this.getTotalGoalsForTeam(matchId, homeTeamId);
      
      // Get total goals for away team  
      const awayGoals = await this.getTotalGoalsForTeam(matchId, awayTeamId);

      console.log(`📊 Match ${matchId} scores: Home ${homeGoals} - Away ${awayGoals}`);

      // Update the matches table with the calculated scores
      const { error: updateError } = await supabase
        .from('matches')
        .update({
          home_score: homeGoals,
          away_score: awayGoals,
          updated_at: new Date().toISOString()
        })
        .eq('id', matchId);

      if (updateError) {
        console.error('Error updating match scores:', updateError);
        throw new Error(`Failed to update match scores: ${updateError.message}`);
      }

      console.log(`✅ Match scores updated successfully: ${homeGoals}-${awayGoals}`);

      // Trigger a custom event to notify UI components to refresh match details
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('matchScoresUpdated', { 
          detail: { matchId } 
        }));
      }

    } catch (error) {
      console.error('❌ Error updating match scores:', error);
      throw error;
    }
  }

  /**
   * Calculate total goals for a team in a match using the same logic as total_goals calculation
   */
  private static async getTotalGoalsForTeam(matchId: string, teamId: string): Promise<number> {
    try {
      // Fetch all goal-related stats for the team
      const { data: stats, error } = await supabase
        .from('scoresheet')
        .select(`
          stat_type_id,
          value,
          stat_type:stat_types(stat_key, display_name)
        `)
        .eq('match_id', matchId)
        .eq('team_id', teamId)
        .in('stat_types.stat_key', [
          'shot_inside_box_goal',
          'shot_outside_box_goal', 
          'penalty_scored',
          'own_goal'
        ]);

      if (error) {
        throw new Error(`Failed to fetch goal stats: ${error.message}`);
      }

      // Calculate total goals using the same logic as calculateAttackStats
      let totalGoals = 0;
      stats?.forEach(stat => {
        if (stat.stat_type && typeof stat.stat_type === 'object' && 'stat_key' in stat.stat_type) {
          const statKey = stat.stat_type.stat_key as string;
          if (['shot_inside_box_goal', 'shot_outside_box_goal', 'penalty_scored', 'own_goal'].includes(statKey)) {
            totalGoals += stat.value || 0;
          }
        }
      });

      return totalGoals;

    } catch (error) {
      console.error('Error calculating total goals for team:', error);
      throw error;
    }
  }

  /**
   * Convenience method to update match scores without running full stats calculation
   * Useful for quick score updates or when called from other services
   */
  static async updateMatchScoresOnly(matchId: string): Promise<void> {
    try {
      // Get match details to find home and away team IDs
      const { data: match, error: matchError } = await supabase
        .from('matches')
        .select('home_team_id, away_team_id')
        .eq('id', matchId)
        .single();

      if (matchError || !match) {
        throw new Error(`Match not found: ${matchError?.message || 'Unknown error'}`);
      }

      await this.updateMatchScores(matchId, match.home_team_id, match.away_team_id);

    } catch (error) {
      console.error('Error updating match scores only:', error);
      throw error;
    }
  }
}

export const calculatedStatsService = CalculatedStatsService;
